import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Ellipsis,
  SquarePen,
  Trash2,
  UserPlus,
  UserRoundCheck,
} from "lucide-react";
import { toast } from "sonner";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import useAuthorized from "hooks/useAuthorized";
import { contactPaths } from "routes/contact/paths";
import ChangeLeadStatusModal from "routes/leads/table/ChangeLeadStatusModal";
import {
  leadAclForScope,
  ownerLabelForScope,
} from "routes/leads/table/leadScope";

export function LeadsTableActions({
  original,
  scope,
  events = [],
  updateStatus,
  deleteLead,
}) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const acl = leadAclForScope(scope);
  const canEdit = useAuthorized([acl.edit]);
  const canDelete = useAuthorized([acl.delete]);

  const handleStatus = async statusId => {
    try {
      await updateStatus(original.id, statusId);
      toast.success("Lead status updated");
    } catch {
      toast.error("Failed to update lead status");
      throw new Error("Failed to update lead status");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLead(original.id);
      toast.success("Lead removed");
      setDeleteOpen(false);
    } catch {
      toast.error("Failed to remove lead");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Lead actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {original.contact_id ? (
            <DropdownMenuItem
              render={<Link to={`/contacts/${original.contact_id}`} />}
            >
              <UserRoundCheck />
              View Contact
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              render={
                <Link
                  to={contactPaths.create({
                    leadId: original.id,
                    fullName: original.name,
                    email: original.email,
                    phone: original.phone,
                  })}
                />
              }
            >
              <UserPlus />
              Create Contact
            </DropdownMenuItem>
          )}
          {canEdit && (
            <DropdownMenuItem onClick={() => setStatusOpen(true)}>
              <SquarePen />
              Change Status
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangeLeadStatusModal
        events={events}
        title="Change Lead Status"
        currentStatusId={original.status?.id}
        open={statusOpen}
        onOpenChange={setStatusOpen}
        onConfirm={handleStatus}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Remove lead?"
        description={`Are you sure you want to remove this lead from the ${ownerLabelForScope(
          scope
        )}?`}
        confirmText="Remove"
        onConfirm={handleDelete}
      />
    </>
  );
}
